import React from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import { BuildingStorefrontIcon } from '@heroicons/react/24/outline';

import CategoryList from 'components/CategoryList';
import normalizeEventDate from 'utils/normalizeEventDate';
import getEventStatus, { isEventCancelled } from 'utils/eventStatus';
import {
  getEventDateRange,
  getTodayDateOnly,
} from 'utils/eventDateRange';

const stripDots = (s) => (typeof s === 'string' ? s.replaceAll('.', '') : s);

// "15 Nov 2026". Month names are capitalised by hand because some locales
// (pt) spell them in lowercase.
const fmtDMMMYYYY = (d, locale) => {
  const m = moment(d);
  if (!m.isValid()) return '';
  const month = stripDots(m.clone().locale(locale).format('MMM'));
  return `${m.date()} ${month.charAt(0).toUpperCase()}${month.slice(1)} ${m.year()}`;
};

const getDateInfo = (rawDate, locale) => {
  const date = normalizeEventDate(rawDate);
  const range = getEventDateRange(date);
  if (!range) return null;

  const mStart = moment(range.startDate);
  const mEnd = moment(range.endDate);

  // The first day only; the days pill beside it gives the span.
  const label = fmtDMMMYYYY(mStart, locale);

  let daysCount;
  if (date?.type === 'segmented' && Array.isArray(date.days)) {
    daysCount = date.days.filter((d) => moment(d?.date).isValid()).length;
  } else {
    daysCount = Math.max(mEnd.diff(mStart, 'days') + 1, 1);
  }

  return { label, daysCount, range };
};

const DateBadge = ({ info }) => {
  const { t } = useTranslation();
  if (!info) return null;
  return (
    <div className="inline-flex items-center gap-1.5 flex-wrap">
      <span className="inline-flex items-center rounded-full bg-white ring-1 ring-line text-gray-600 font-bold tracking-wide text-[11px] px-2.5 py-1 whitespace-nowrap">
        {info.label}
      </span>
      {Number.isFinite(info.daysCount) && info.daysCount > 0 && (
        <span className="inline-flex items-center rounded-full bg-ink-strong text-white font-semibold text-[10.5px] px-2 py-1 whitespace-nowrap">
          {info.daysCount}{' '}
          {info.daysCount === 1 ? t('common.day') : t('common.days')}
        </span>
      )}
    </div>
  );
};

const StateBadge = ({ event }) => {
  const { t } = useTranslation();
  const status = getEventStatus(event?.date);
  if (!status) return null;

  if (status === 'live' || event?.state === 'live') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 text-[10px] font-bold uppercase tracking-[0.08em]">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        {t('listing.event_status_live')}
      </span>
    );
  }

  if (status === 'finished') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-paper text-gray-500 ring-1 ring-line text-[10px] font-bold uppercase tracking-[0.08em]">
        {t('listing.event_status_past')}
      </span>
    );
  }

  // upcoming — show "In Nd" only if 1-14 days away
  const range = getEventDateRange(event?.date);
  if (!range) return null;
  // getEventDateRange returns YYYY-MM-DD strings (not Date objects), so compute
  // the delta with moment rather than Date#getTime — calling .getTime() on a
  // string throws and blanks the whole list view (T2B-355).
  const today = getTodayDateOnly();
  const days = moment(range.startDate, 'YYYY-MM-DD').diff(
    moment(today, 'YYYY-MM-DD'),
    'days'
  );
  if (days > 0 && days <= 14) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-brand-soft text-amber-900 ring-1 ring-amber-200 text-[10px] font-bold uppercase tracking-[0.08em]">
        {t('listing.event_status_in_days', { count: days })}
      </span>
    );
  }
  return null;
};

const FillBar = ({ current, total, compact = false }) => {
  const { t } = useTranslation();
  const safeCurrent = Number(current) || 0;
  const safeTotal = Number(total) || 0;
  const pct = safeTotal > 0 ? Math.min((safeCurrent / safeTotal) * 100, 100) : 0;

  let label;
  let bar;
  let labelColor;
  if (safeTotal === 0 || safeCurrent === 0) {
    label = t('listing.fill_spaces_open');
    bar = 'bg-emerald-500';
    labelColor = 'text-emerald-700';
  } else if (pct >= 100) {
    label = t('listing.fill_sold_out');
    bar = 'bg-rose-500';
    labelColor = 'text-rose-600';
  } else if (pct >= 80) {
    label = t('listing.fill_almost_full');
    bar = 'bg-amber-500';
    labelColor = 'text-amber-700';
  } else if (pct > 50) {
    label = t('listing.fill_filling_up');
    bar = 'bg-amber-500';
    labelColor = 'text-amber-700';
  } else {
    label = t('listing.fill_spaces_open');
    bar = 'bg-emerald-500';
    labelColor = 'text-emerald-700';
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-1">
        <span
          className={`text-[10px] uppercase tracking-[0.1em] font-bold ${
            compact ? 'truncate min-w-0 mr-2' : ''
          } ${labelColor}`}
        >
          {label}
        </span>
        {safeTotal > 0 && (
          <span className="text-[11px] font-bold tabular-nums text-gray-700 shrink-0">
            {safeCurrent}
            <span className="text-gray-400 font-medium">/{safeTotal}</span>
          </span>
        )}
      </div>
      <div className="w-full h-1 rounded-full overflow-hidden bg-paper ring-1 ring-line">
        <div
          className={`h-full rounded-full transition-all ${bar}`}
          style={{ width: `${Math.max(pct, safeCurrent > 0 ? 4 : 0)}%` }}
        />
      </div>
    </div>
  );
};

const PriceChip = ({ price }) => {
  const { t } = useTranslation();
  if (price == null) return null;
  return (
    <div className="inline-flex items-baseline gap-1 rounded-full px-3 py-1 text-[12px] bg-brand-soft text-ink-strong ring-1 ring-amber-200 font-bold tabular-nums">
      <span className="text-[9px] uppercase tracking-[0.12em] font-bold opacity-70 -translate-y-px">
        {t('common.from_price')}
      </span>
      <span>{price}€</span>
    </div>
  );
};

const DummyRibbon = ({ label = t('listing.example') }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none z-10 rounded-2xl">
    <div
      className="absolute bg-amber-500 text-white text-[11px] font-bold uppercase tracking-widest text-center leading-[26px] h-[26px]"
      style={{
        top: '16px',
        right: '-32px',
        width: '160px',
        transform: 'rotate(45deg)',
        boxShadow: '0 2px 6px rgba(0,0,0,.18)',
      }}
    >
      {label}
    </div>
  </div>
);

// Phone/tablet variant of StateBadge: upcoming events within a month read as
// plain text ("In 13 days"); live and past keep their pill.
const StartsIn = ({ event }) => {
  const { t } = useTranslation();
  const status = getEventStatus(event?.date);
  if (status === 'live' || status === 'finished' || event?.state === 'live') {
    return <StateBadge event={event} />;
  }
  const range = getEventDateRange(event?.date);
  if (!range) return null;
  const days = moment(range.startDate, 'YYYY-MM-DD').diff(
    moment(getTodayDateOnly(), 'YYYY-MM-DD'),
    'days'
  );
  if (days <= 0 || days > 30) return null;
  return (
    <span className="text-[12px] font-semibold text-amber-700 whitespace-nowrap">
      {t('listing.starts_in_days', { count: days })}
    </span>
  );
};

const EventCardList = ({ event, hasNavigation, isGuest, compact = false }) => {
  const { i18n } = useTranslation();
  if (!event) return null;

  const lowestPrice =
    event?.lowestPrice ??
    event?.pricing?.lowestPrice ??
    event?.priceFrom ??
    null;

  // Live, past and cancelled events don't advertise availability: bookings
  // are closed once the event is running, over or called off, so the fill bar
  // would only be noise (T2B-397).
  const dateStatus = getEventStatus(event?.date);
  const isLive = dateStatus === 'live' || event?.state === 'live';
  const hideFillBar =
    isLive || dateStatus === 'finished' || isEventCancelled(event);
  // Live events drop the price as well: the door is already closed, so a
  // "from X€" chip advertises a booking that can no longer be made (T2B-412).
  const hidePrice = isLive;

  const hasPrice = lowestPrice !== null && lowestPrice !== undefined;
  // Once the bar and the price are both gone the rail holds nothing, so drop
  // it (and its grid column) instead of leaving a blank gutter on the row.
  const showPriceAndProgressBar =
    !isGuest && hasPrice && !(hideFillBar && hidePrice);

  const info = getDateInfo(event.date, i18n?.language || 'en');
  const locationLabel =
    event.city && event.country
      ? `${event.city}, ${event.country}`
      : event.city || event.country || event.location || '';

  const eventDetailUrl = `/event/${event.slug}`;

  const gridColsLg = showPriceAndProgressBar
    ? 'lg:[grid-template-columns:minmax(110px,160px)_56px_minmax(0,1fr)_220px_220px]'
    : 'lg:[grid-template-columns:minmax(110px,160px)_56px_minmax(0,1fr)_minmax(220px,1fr)]';

  // Below desktop: cover thumbnail with the logo on it, then title, dates,
  // location + categories and, when signed in, availability and price.
  const CompactCard = () => (
    <div className="relative w-full bg-white rounded-2xl ring-1 ring-line shadow-card-listing p-2.5 flex gap-3 overflow-hidden">
      {event.isDummy && <DummyRibbon />}

      {/* Signed out the row has no availability/price line, so the thumb
          shrinks with it rather than leaving a gap under the location. */}
      <div
        className={`relative self-stretch rounded-xl overflow-hidden bg-paper shrink-0 ${
          showPriceAndProgressBar
            ? 'w-[104px] sm:w-[120px] min-h-[104px]'
            : 'w-[88px] min-h-[88px]'
        }`}
      >
        {event?.landscapeImage || event?.avatar ? (
          <img
            src={event.landscapeImage || event.avatar}
            alt={event.name || 'Event'}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 grid place-items-center">
            <BuildingStorefrontIcon className="w-8 h-8 text-gray-300" />
          </div>
        )}
        {event?.avatar && (
          <div className="absolute left-2 bottom-2 w-10 h-10 rounded-full overflow-hidden ring-2 ring-white bg-white shadow-sm">
            <img
              src={event.avatar}
              alt=""
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0 flex flex-col gap-1.5 py-0.5">
        <h3 className="font-extrabold text-[15px] leading-tight text-ink-strong truncate tracking-tight">
          {event.name}
        </h3>
        <div className="flex flex-wrap items-center gap-1.5">
          {info && (
            <>
              <span className="inline-flex items-center rounded-full bg-white ring-1 ring-line text-gray-600 font-bold tracking-wide px-2 py-0.5 text-[10px] whitespace-nowrap">
                {info.label}
              </span>
              {Number.isFinite(info.daysCount) && info.daysCount > 0 && (
                <span className="inline-flex items-center rounded-full bg-ink-strong text-white font-semibold px-1.5 py-0.5 text-[10px] whitespace-nowrap">
                  {info.daysCount}{' '}
                  {info.daysCount === 1 ? t('common.day') : t('common.days')}
                </span>
              )}
            </>
          )}
          <StartsIn event={event} />
        </div>
        <div className="flex items-center justify-between gap-2 min-w-0">
          {locationLabel && (
            <p className="m-0 text-[12px] text-gray-500 truncate">
              {locationLabel}
            </p>
          )}
          <div className="ml-auto shrink-0">
            <CategoryList
              categories={event.categoriesAllowed}
              onlyMacro
              macroIconClass="w-[18px] h-[18px]"
              containerClass="flex flex-row items-center justify-end gap-1 overflow-visible"
            />
          </div>
        </div>

        {showPriceAndProgressBar && (
          <div className="mt-auto pt-2 border-t border-line/70 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              {!hideFillBar && (
                <FillBar
                  current={event.bookedSpaces}
                  total={event.totalSpaces}
                  compact
                />
              )}
            </div>
            {!hidePrice && <PriceChip price={lowestPrice} />}
          </div>
        )}
      </div>
    </div>
  );

  const Card = () => (
    <div
      className={`relative w-full bg-white rounded-2xl ring-1 ring-line shadow-card-listing hover:shadow-lg transition px-3 py-3
                  grid grid-cols-1 gap-4 items-center
                  ${gridColsLg}`}
    >
      {event.isDummy && <DummyRibbon />}

      {/* Date badge (desktop only) */}
      <div className="hidden lg:flex items-center justify-center">
        <DateBadge info={info} />
      </div>

      {/* Thumb */}
      <div className="relative w-12 h-12 lg:w-14 lg:h-14 rounded-2xl overflow-hidden ring-1 ring-line shrink-0 bg-paper">
        {event?.avatar ? (
          <img
            src={event.avatar}
            alt={event.name || 'Event'}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 grid place-items-center">
            <BuildingStorefrontIcon className="w-6 h-6 text-gray-300" />
          </div>
        )}
      </div>

      {/* Title + meta */}
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-extrabold text-[15px] text-ink-strong truncate tracking-tight">
            {event.name}
          </h3>
          <StateBadge event={event} />
        </div>
        {locationLabel && (
          <p className="text-[12px] text-gray-500 truncate">{locationLabel}</p>
        )}
        {/* Mobile-only date chips */}
        {info && (
          <div className="flex lg:hidden flex-wrap items-center gap-1.5 mt-1">
            <span className="inline-flex items-center rounded-full bg-white ring-1 ring-line text-gray-600 font-bold tracking-wide px-2.5 py-0.5 text-[10px] whitespace-nowrap">
              {info.label}
            </span>
            {Number.isFinite(info.daysCount) && info.daysCount > 0 && (
              <span className="inline-flex items-center rounded-full bg-ink-strong text-white font-semibold px-2 py-0.5 text-[10px] whitespace-nowrap">
                {info.daysCount}{' '}
                {info.daysCount === 1 ? t('common.day') : t('common.days')}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Categories */}
      <div className="hidden lg:flex items-center justify-end">
        <CategoryList
          categories={event.categoriesAllowed}
          onlyMacro
          withLabel
          macroIconClass="w-6 h-6"
          containerClass="flex flex-row flex-wrap items-center justify-end gap-x-2 gap-y-1 overflow-visible"
        />
      </div>

      {/* Right rail: fill + price (authenticated only) */}
      {showPriceAndProgressBar && (
        <div className="flex items-center justify-between lg:justify-end gap-3">
          <div className="flex-1 lg:max-w-[120px]">
            {!hideFillBar && (
              <FillBar
                current={event.bookedSpaces}
                total={event.totalSpaces}
              />
            )}
          </div>
          {!hidePrice && <PriceChip price={lowestPrice} />}
        </div>
      )}
    </div>
  );

  const Row = compact ? CompactCard : Card;

  return hasNavigation ? (
    <Link to={eventDetailUrl} className="no-underline text-current block">
      <Row />
    </Link>
  ) : (
    <Row />
  );
};

export default EventCardList;
